"use client";

import * as React from "react";
import { cn } from "../lib/utils";
import { Button } from "./button";
import { Input } from "./input";
import { Skeleton } from "./skeleton";

// ============ Types ============

export interface DataTableColumn<T> {
  key: string;
  header: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
  hidden?: boolean;
}

export type SortDirection = "asc" | "desc" | null;

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  loading?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  sortColumn?: string;
  sortDirection?: SortDirection;
  onSort?: (column: string, direction: SortDirection) => void;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  actions?: React.ReactNode;
  rowKey?: (row: T) => string;
  selectedRows?: Set<string>;
  onSelectRow?: (id: string) => void;
  onSelectAll?: () => void;
  className?: string;
  pageSizeOptions?: number[];
}

// ============ Search with debounce ============

function useDebounce(value: string, delay: number) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ============ DataTable Component ============

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  total = 0,
  page = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  loading,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Buscar...",
  sortColumn,
  sortDirection,
  onSort,
  onRowClick,
  emptyMessage = "Nenhum registro encontrado",
  actions,
  rowKey,
  selectedRows,
  onSelectRow,
  onSelectAll,
  className,
  pageSizeOptions = [10, 20, 50],
}: DataTableProps<T>) {
  const [localSearch, setLocalSearch] = React.useState(searchValue || "");
  const debouncedSearch = useDebounce(localSearch, 300);

  React.useEffect(() => {
    if (onSearchChange && debouncedSearch !== searchValue) {
      onSearchChange(debouncedSearch);
    }
  }, [debouncedSearch]);

  // Sync external search value
  React.useEffect(() => {
    if (searchValue !== undefined && searchValue !== localSearch) {
      setLocalSearch(searchValue);
    }
  }, [searchValue]);

  const visibleColumns = columns.filter((c) => !c.hidden);
  const totalPages = Math.ceil(total / pageSize);
  const hasSelection = selectedRows !== undefined && onSelectRow !== undefined;
  const getKey = rowKey || ((row: T) => (row as any).id || String(Math.random()));

  const handleSort = (col: DataTableColumn<T>) => {
    if (!col.sortable || !onSort) return;
    const newDir: SortDirection =
      sortColumn === col.key
        ? sortDirection === "asc"
          ? "desc"
          : sortDirection === "desc"
            ? null
            : "asc"
        : "asc";
    onSort(col.key, newDir);
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Toolbar: Search + Actions */}
      {(onSearchChange || actions) && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {onSearchChange && (
            <div className="w-full sm:max-w-xs">
              <Input
                placeholder={searchPlaceholder}
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                className="h-9"
              />
            </div>
          )}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                {hasSelection && (
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={
                        data.length > 0 &&
                        selectedRows!.size === data.length
                      }
                      onChange={onSelectAll}
                      className="rounded border-border"
                    />
                  </th>
                )}
                {visibleColumns.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      "px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground",
                      col.sortable && "cursor-pointer select-none hover:text-foreground",
                      col.headerClassName
                    )}
                    onClick={() => handleSort(col)}
                  >
                    <div className="flex items-center gap-1">
                      {col.header}
                      {col.sortable && sortColumn === col.key && (
                        <SortIcon direction={sortDirection || null} />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {loading ? (
                Array.from({ length: Math.min(pageSize, 5) }).map((_, i) => (
                  <tr key={i}>
                    {hasSelection && (
                      <td className="px-4 py-3">
                        <Skeleton className="h-4 w-4" />
                      </td>
                    )}
                    {visibleColumns.map((col) => (
                      <td key={col.key} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumns.length + (hasSelection ? 1 : 0)}
                    className="px-4 py-12 text-center text-muted-foreground"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                data.map((row) => {
                  const key = getKey(row);
                  return (
                    <tr
                      key={key}
                      className={cn(
                        "transition-colors hover:bg-muted/30",
                        onRowClick && "cursor-pointer",
                        selectedRows?.has(key) && "bg-primary-50 dark:bg-primary-900/10"
                      )}
                      onClick={() => onRowClick?.(row)}
                    >
                      {hasSelection && (
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedRows!.has(key)}
                            onChange={() => onSelectRow!(key)}
                            className="rounded border-border"
                          />
                        </td>
                      )}
                      {visibleColumns.map((col) => (
                        <td
                          key={col.key}
                          className={cn("px-4 py-3", col.className)}
                        >
                          {col.render
                            ? col.render(row)
                            : (row as any)[col.key] ?? "-"}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer: Page size + Pagination */}
      {(totalPages > 1 || onPageSizeChange) && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {total > 0 && (
              <span>
                {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, total)} de {total}
              </span>
            )}
            {onPageSizeChange && (
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs"
              >
                {pageSizeOptions.map((s) => (
                  <option key={s} value={s}>
                    {s} / pag
                  </option>
                ))}
              </select>
            )}
          </div>
          {totalPages > 1 && onPageChange && (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => onPageChange(1)}
                className="h-8 w-8 p-0"
              >
                <ChevronsLeftIcon />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
                className="h-8 w-8 p-0"
              >
                <ChevronLeftIcon />
              </Button>
              {generatePageNumbers(page, totalPages).map((p, i) =>
                p === "..." ? (
                  <span key={`ellipsis-${i}`} className="px-1 text-muted-foreground">
                    ...
                  </span>
                ) : (
                  <Button
                    key={p}
                    variant={p === page ? "primary" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(p as number)}
                    className="h-8 w-8 p-0 text-xs"
                  >
                    {p}
                  </Button>
                )
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
                className="h-8 w-8 p-0"
              >
                <ChevronRightIcon />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => onPageChange(totalPages)}
                className="h-8 w-8 p-0"
              >
                <ChevronsRightIcon />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============ Helpers ============

function generatePageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "...")[] = [];
  if (current <= 4) {
    for (let i = 1; i <= 5; i++) pages.push(i);
    pages.push("...", total);
  } else if (current >= total - 3) {
    pages.push(1, "...");
    for (let i = total - 4; i <= total; i++) pages.push(i);
  } else {
    pages.push(1, "...", current - 1, current, current + 1, "...", total);
  }
  return pages;
}

// ============ Icons ============

function SortIcon({ direction }: { direction: SortDirection }) {
  if (!direction) return null;
  return (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d={direction === "asc" ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
      />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function ChevronsLeftIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
    </svg>
  );
}

function ChevronsRightIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
    </svg>
  );
}
